import React, { useMemo, useState, useEffect, memo } from "react";
import useValidate from "../../../store/hooks/useValidator";
import { useDispatch } from "react-redux";
import moment from "moment";
import { callApiAction } from "../../../store/actions/commonAction";
import { closeModal, openModal } from "../../../store/actions/modalAction";
import HearNUCreateUI from "./HearNUCreateUI";
import { AFFECTED_EAR, SNACK_BAR_VARIETNS } from "../../../utils/constants";
import {
  createPatientApi,
  getPatientByIdApi,
  updatePatientApi,
} from "../../../apis/patient.api";
import { callSnackBar } from "../../../store/actions/snackbarAction";
import HeadMeasurementModal from "../../customhearnuform/HeadMeasurementModal";
import InstructionModal from "../../../pages/customhearnuform/InstructionModal";

const HearNUCreateController = ({ callBack, id }) => {
  const validate = useValidate();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [originalDocument, setOriginalDocument] = useState({});
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState({
    err: "",
    id,
    name: "",
    patient_of: [],
    age: 0,
    dob: moment(),
    gender: "",
    patient_address: "",
    city: "",
    state: "",
    patient_phone: "",
    patient_guardian_no: "",
    affected_ear: AFFECTED_EAR.BOTH,
    file: "",
    audiogram_report: null,
    ref_image_front_Head: null,
    ref_image_left_Head: null,
    ref_image_right_Head: null,
    lf: {
      lf250: 0,
      lf500: 0,
      lf1k: 0,
      lf2k: 0,
      lf4k: 0,
      lf6k: 0,
    },
    rf: {
      rf250: 0,
      rf500: 0,
      rf1k: 0,
      rf2k: 0,
      rf4k: 0,
      rf6k: 0,
    },
  });

  const steps = [
    "Patient Details",
    "Patient Type",
    "Audiogram",
    "Upload References",
  ];

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const updateFunction = async (e) => {
    e.preventDefault();
    let updatedData = { id };
    for (let field in fields) {
      if (
        originalDocument[field] &&
        fields[field] &&
        fields[field] !== originalDocument[field]
      ) {
        updatedData[field] = fields[field];
      }
      if (!originalDocument[field]) {
        updatedData[field] = fields[field];
      }
    }
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await updatePatientApi(updatedData),
        async (response) => {
          await callBack(updatedData);
          setLoading(false);
          dispatch(closeModal("HearNUUpdate"));
          dispatch(
            callSnackBar("Updated Successfully", SNACK_BAR_VARIETNS?.success)
          );
        },
        (err) => {
          setLoading(false);
          setFields({ ...fields, err });
        }
      )
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validationSchemaArr = [
      validationSchemaForPatientDetails,
      validationSchemaPatientType,
      validationSchemaAudiograms,
      validationSchemaForUploadReferences,
    ];

    const validationResponse = validate(validationSchemaArr[activeStep]);
    if (validationResponse === true) {
      setActiveStep(activeStep + 1);

      if (activeStep === 3) {
        id ? updateFunction(e) : createFunction(e);
        return;
      }
    } else {
      setFields((prevFields) => ({ ...prevFields, err: validationResponse }));
    }
  };

  const createFunction = async (e) => {
    e.preventDefault();
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await createPatientApi({ ...fields }),
        async (response) => {
          setLoading(false);
          dispatch(
            callSnackBar("Patient Created Successfully", SNACK_BAR_VARIETNS?.success)
          );
          callBack();
          dispatch(closeModal("HearNUCreate"));
        },
        (err) => {
          setLoading(false);
          dispatch(callSnackBar("Error", SNACK_BAR_VARIETNS?.error));
          setFields((prevFields) => ({ ...prevFields, err }));
        }
      )
    );
  };

  const handleCaptureMeasurement = () => {
    dispatch(
      openModal(
        <InstructionModal
          onReady={() => {
            dispatch(closeModal("instruction-modal")); 
            setTimeout(() => {
              dispatch(
                openModal(
                  <HeadMeasurementModal
                    onClose={() => dispatch(closeModal())}
                    onMeasurementComplete={(values) => {
                      console.log("📦 Measurement received from modal:", values);
                      setFields((prev) => ({
                        ...prev,
                        upperHeadCircumference: values["Temporal Arc (cm)"] || "",
                        temporalBoneWidth: values["Temporal Width (cm)"] || "",
                        foreheadToBackheadCircumference:
                          values["Head Circumference (cm)"] || "",
                      }));
                    }}
                  />,
                  "md",
                  true
                )
              );
            }, 300);
          }}
        />,
        "sm",
        true,
        "instruction-modal"
      )
    );
  };

  const fetchById = (id) => {
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await getPatientByIdApi({ id }),
        async (response) => {
          setFields({ ...fields, ...response });
          setOriginalDocument(response);
          setLoading(false);
        },
        (err) => {
          setFields({ ...fields, err });
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    if (id) {
      fetchById(id);
    }
  }, [id]);

  const validationSchemaForPatientDetails = useMemo(
    () => [
      { required: true, value: fields?.name, field: "Patient Name" },
      { required: true, value: fields?.age, field: "Age" },
      { required: true, value: fields?.gender, field: "Gender" },
      {
        required: true,
        value: fields.dob,
        field: "dob",
        message: "Date of Birth",
      },
      { required: true, value: fields?.patient_address, field: "Address" },
      { required: true, value: fields?.state, field: "State" },
      { required: true, value: fields?.city, field: "City" },
      {
        required: true,
        value: fields?.patient_guardian_no,
        field: "Guardian Phone No",
      },
    ],
    [fields]
  );

  const validationSchemaPatientType = React.useMemo(
    () => [
      {
        required: true,
        isArray: true,
        value: fields?.patient_of,
        field: "Patient Type",
        message: "Please select at least one Patient Type.",
      },
    ],
    [fields]
  );

  const validationSchemaAudiograms = React.useMemo(() => {
    const validationRules = [];

    if (
      fields?.affected_ear === AFFECTED_EAR?.LEFT ||
      fields?.affected_ear === AFFECTED_EAR?.BOTH
    ) {
      validationRules.push(
        { value: fields?.lf?.lf250, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.lf?.lf500, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.lf?.lf1k, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.lf?.lf2k, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.lf?.lf4k, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.lf?.lf6k, field: "Incomplete Audiogram", audiogram: true }
      );
    }

    if (
      fields?.affected_ear === AFFECTED_EAR?.RIGHT ||
      fields?.affected_ear === AFFECTED_EAR?.BOTH
    ) {
      validationRules.push(
        { value: fields?.rf?.rf250, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.rf?.rf500, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.rf?.rf1k, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.rf?.rf2k, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.rf?.rf4k, field: "Incomplete Audiogram", audiogram: true },
        { value: fields?.rf?.rf6k, field: "Incomplete Audiogram", audiogram: true }
      );
    }
    return validationRules;
  }, [fields?.affected_ear, fields?.lf, fields?.rf]);

  const validationSchemaForUploadReferences = useMemo(
    () => [
      {
        required: true,
        value: fields?.ref_image_front_Head,
        field: "Front Head Reference Image",
      },
      {
        required:
          fields?.affected_ear === AFFECTED_EAR?.LEFT ||
          fields?.affected_ear === AFFECTED_EAR?.BOTH,
        value: fields?.ref_image_left_Head,
        field: "Left Side Head Reference Image",
      },
      {
        required:
          fields?.affected_ear === AFFECTED_EAR?.RIGHT ||
          fields?.affected_ear === AFFECTED_EAR?.BOTH,
        value: fields?.ref_image_right_Head,
        field: "Right Side Head Reference Image",
      },
      {
        required: true,
        value: fields?.upperHeadCircumference,
        field: "Temporal Arc",
      },
      {
        required: true,
        value: fields?.temporalBoneWidth,
        field: "Temporal Width",
      },
      {
        required: true,
        value: fields?.foreheadToBackheadCircumference,
        field: "Head Circumference",
      },
    ],
    [fields]
  );

  return (
    <HearNUCreateUI
      loading={loading}
      fields={fields}
      onSubmit={onSubmit}
      setFields={setFields}
      activeStep={activeStep}
      steps={steps}
      handleBack={handleBack}
      handleCaptureMeasurement={handleCaptureMeasurement}
    />
  );
};

export default memo(HearNUCreateController);
